import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { reloadOnChunkError } from './app-update.service';

/** Delay before showing the overlay so cached chunk loads do not flash a spinner. */
const SHOW_DELAY_MS = 120;

/**
 * Tracks pending lazy chunk loads (route navigations and dynamic imports) and
 * exposes a visibility signal for a global loading overlay.
 */
@Injectable({ providedIn: 'root' })
export class ChunkLoadingService {
  private static instance: ChunkLoadingService | undefined;

  private readonly pending = signal(0);
  private readonly delayedVisible = signal(false);
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private navigationDepth = 0;

  /** True once pending work has lasted longer than {@link SHOW_DELAY_MS}. */
  readonly isVisible = computed(() => this.delayedVisible());

  constructor() {
    ChunkLoadingService.instance = this;

    const router = inject(Router);
    const destroyRef = inject(DestroyRef);

    // Cover a navigation that started before this service was constructed.
    if (router.getCurrentNavigation()) {
      this.navigationDepth = 1;
      this.begin();
    }

    router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.navigationDepth += 1;
          if (this.navigationDepth === 1) {
            this.begin();
          }
          return;
        }

        if (this.navigationDepth > 0) {
          this.navigationDepth -= 1;
          if (this.navigationDepth === 0) {
            this.end();
          }
        }
      });
  }

  /** Safe accessor for plain async helpers outside an injection context. */
  static getOptional(): ChunkLoadingService | undefined {
    return ChunkLoadingService.instance;
  }

  begin(): void {
    this.pending.update((n) => n + 1);
    if (this.pending() === 1 && this.showTimer === null) {
      this.showTimer = setTimeout(() => {
        this.showTimer = null;
        if (this.pending() > 0) {
          this.delayedVisible.set(true);
        }
      }, SHOW_DELAY_MS);
    }
  }

  end(): void {
    this.pending.update((n) => Math.max(0, n - 1));
    if (this.pending() === 0) {
      if (this.showTimer !== null) {
        clearTimeout(this.showTimer);
        this.showTimer = null;
      }
      this.delayedVisible.set(false);
    }
  }

  /** Increments the pending counter for the duration of `work`. */
  async wrap<T>(work: () => Promise<T>): Promise<T> {
    this.begin();
    try {
      return await work();
    } finally {
      this.end();
    }
  }
}

/**
 * Wraps a dynamic `import()` (or any promise) so the global chunk loader
 * shows while it is in flight. No-ops if the service is not yet constructed.
 */
export function wrapChunkLoad<T>(promise: Promise<T>): Promise<T> {
  const guarded = reloadOnChunkError(promise);
  const service = ChunkLoadingService.getOptional();
  if (!service) {
    return guarded;
  }
  service.begin();
  return guarded.finally(() => service.end());
}
